import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { matchRoute } from '@/pages/page-registry';

type TabHistoryEntry = {
  pathname: string;
  search: string;
};

export type OpenTab = {
  id: string;
  pathname: string;
  search: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  history: TabHistoryEntry[];
  historyIndex: number;
};

const STORAGE_KEY = 'ats-spa.tabs.v2';
const ACTIVE_STORAGE_KEY = 'ats-spa.active-tab.v2';
const DEFAULT_TAB_PATH = '/new-tab';
const STANDALONE_HOME_PATH = '/';
// Routes that render in the shell's fullscreen frame instead of inside a tab.
// Sidebar nav into one of these must NOT replace the active tab's URL —
// otherwise the tab gets stuck pointing to a fullscreen route, which can only
// be opened (no sidebar inside fullscreen to navigate it elsewhere).
// Keep this list in sync with FULLSCREEN_PATH_PREFIXES in AppShell.tsx.
const FULLSCREEN_TAB_EXEMPT_PREFIXES = ['/settings', '/brain'];

function isFullscreenTabExempt(pathname: string) {
  return FULLSCREEN_TAB_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

type PendingTabNavigation = {
  tabId: string | null;
  preserveHistory: boolean;
};

function createId() {
  return globalThis.crypto?.randomUUID?.() || `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getTitle(pathname: string) {
  const route = matchRoute(pathname);
  return route?.tabLabel || route?.title || 'New Tab';
}

function sameLocation(a: TabHistoryEntry, b: TabHistoryEntry) {
  return a.pathname === b.pathname && a.search === b.search;
}

function toUrl(entry: TabHistoryEntry) {
  return `${entry.pathname}${entry.search}`;
}

function clampIndex(index: unknown, length: number) {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(typeof index === 'number' ? index : length - 1, length - 1));
}

function normalizeTab(tab: Partial<OpenTab>): OpenTab | null {
  // Drop tabs at fullscreen URLs (legacy state from before the fullscreen-tab
  // exemption landed). Those tabs are unusable — they always re-open fullscreen
  // with no way to navigate elsewhere.
  if (!tab.id || !tab.pathname || tab.pathname === STANDALONE_HOME_PATH || isFullscreenTabExempt(tab.pathname)) return null;

  const pathname = tab.pathname;
  const search = typeof tab.search === 'string' ? tab.search : '';
  const currentEntry = { pathname, search };
  const storedHistory = Array.isArray(tab.history)
    ? tab.history.filter((entry) => entry?.pathname && entry.pathname !== STANDALONE_HOME_PATH)
    : [];
  let history = storedHistory.length > 0 ? storedHistory : [currentEntry];
  let historyIndex = clampIndex(tab.historyIndex, history.length);

  if (!sameLocation(history[historyIndex], currentEntry)) {
    history = [...history.slice(0, historyIndex + 1), currentEntry];
    historyIndex = history.length - 1;
  }

  return {
    id: tab.id,
    pathname,
    search,
    title: tab.title || getTitle(pathname),
    createdAt: typeof tab.createdAt === 'number' ? tab.createdAt : Date.now(),
    updatedAt: typeof tab.updatedAt === 'number' ? tab.updatedAt : Date.now(),
    history,
    historyIndex,
  };
}

function createTab(pathname = DEFAULT_TAB_PATH, search = ''): OpenTab {
  const now = Date.now();
  const history = [{ pathname, search }];

  return {
    id: createId(),
    pathname,
    search,
    title: getTitle(pathname),
    createdAt: now,
    updatedAt: now,
    history,
    historyIndex: 0,
  };
}

function readTabs() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as OpenTab[];
    return Array.isArray(parsed)
      ? parsed.map(normalizeTab).filter((tab): tab is OpenTab => Boolean(tab))
      : [];
  } catch {
    return [];
  }
}

function hasSavedTabState() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function writeTabs(tabs: OpenTab[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
}

function readActiveTabId() {
  return window.localStorage.getItem(ACTIVE_STORAGE_KEY);
}

function writeActiveTabId(id: string | null) {
  if (id) {
    window.localStorage.setItem(ACTIVE_STORAGE_KEY, id);
  } else {
    window.localStorage.removeItem(ACTIVE_STORAGE_KEY);
  }
}

export function useTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingNavigationRef = useRef<PendingTabNavigation | null>(null);
  const [initialTabs] = useState(() => {
    const stored = readTabs();
    // Deep-linking into a fullscreen route (e.g. /brain/memory, /settings/general)
    // shouldn't seed a tab — those routes render outside the tab system.
    const landingIsFullscreen = isFullscreenTabExempt(window.location.pathname);
    const shouldRestoreEmptyTabs =
      stored.length === 0 && hasSavedTabState() && (window.location.pathname === STANDALONE_HOME_PATH || landingIsFullscreen);
    const initialPath =
      window.location.pathname === STANDALONE_HOME_PATH
        ? DEFAULT_TAB_PATH
        : window.location.pathname || DEFAULT_TAB_PATH;
    const tabs = stored.length > 0
      ? stored
      : shouldRestoreEmptyTabs || landingIsFullscreen
        ? []
      : [createTab(initialPath, initialPath === DEFAULT_TAB_PATH ? '' : window.location.search)];
    const storedActive = readActiveTabId();
    let activeTabId = '';

    if (tabs.length > 0) {
      activeTabId = storedActive && tabs.some((tab) => tab.id === storedActive) ? storedActive : tabs[0].id;
    }

    return { tabs, activeTabId };
  });
  const [tabs, setTabs] = useState<OpenTab[]>(initialTabs.tabs);
  const [activeTabId, setActiveTabId] = useState<string>(initialTabs.activeTabId);

  useEffect(() => {
    setTabs((current) => {
      const next = current.filter((tab) => tab.pathname !== STANDALONE_HOME_PATH);

      if (next.length === current.length) return current;

      const safeTabs = next.length > 0 ? next : [createTab(DEFAULT_TAB_PATH)];
      writeTabs(safeTabs);

      if (!safeTabs.some((tab) => tab.id === activeTabId)) {
        setActiveTabId(safeTabs[0].id);
      }

      return safeTabs;
    });
  }, [activeTabId]);

  useEffect(() => {
    if (location.pathname === STANDALONE_HOME_PATH) {
      pendingNavigationRef.current = null;
      return;
    }

    // Fullscreen routes (/brain/*, /settings/*) live outside the tab system.
    // Don't update the active tab's URL when navigating into one — otherwise
    // the tab gets permanently stuck pointing to a fullscreen path that has no
    // sidebar to navigate it back out. The shell's fullscreen frame handles
    // exit via FullscreenTopBar's Back button (→ lastNonFullscreenLocation).
    if (isFullscreenTabExempt(location.pathname)) {
      pendingNavigationRef.current = null;
      return;
    }

    const pendingNavigation = pendingNavigationRef.current;
    if (pendingNavigation?.tabId === null) return;

    let nextActiveTabId = pendingNavigation?.tabId || activeTabId;

    setTabs((current) => {
      const existing = current.some((tab) => tab.id === nextActiveTabId);
      const newTab = existing ? null : createTab(location.pathname, location.search);
      const base = newTab ? [...current, newTab] : current;
      nextActiveTabId = newTab?.id || nextActiveTabId;
      const next = base.map((tab) => {
        if (tab.id !== nextActiveTabId) return tab;
        const nextEntry = { pathname: location.pathname, search: location.search };
        const currentHistory = tab.history.length > 0 ? tab.history : [{ pathname: tab.pathname, search: tab.search }];
        const currentIndex = clampIndex(tab.historyIndex, currentHistory.length);
        const shouldPreserveHistory = pendingNavigation?.tabId === tab.id && pendingNavigation.preserveHistory;
        const nextHistory =
          shouldPreserveHistory || sameLocation(currentHistory[currentIndex], nextEntry)
            ? currentHistory
            : [...currentHistory.slice(0, currentIndex + 1), nextEntry];
        const nextHistoryIndex =
          shouldPreserveHistory || sameLocation(currentHistory[currentIndex], nextEntry)
            ? currentIndex
            : nextHistory.length - 1;

        return {
          ...tab,
          pathname: location.pathname,
          search: location.search,
          title: getTitle(location.pathname),
          updatedAt: Date.now(),
          history: nextHistory,
          historyIndex: nextHistoryIndex,
        };
      });

      writeTabs(next);
      return next;
    });

    pendingNavigationRef.current = null;

    if (nextActiveTabId !== activeTabId) {
      setActiveTabId(nextActiveTabId);
    }
  }, [activeTabId, location.pathname, location.search]);

  useEffect(() => {
    writeActiveTabId(activeTabId);
  }, [activeTabId]);

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId) || tabs[0] || null,
    [activeTabId, tabs],
  );

  const activeIndex = useMemo(
    () => tabs.findIndex((tab) => tab.id === activeTabId),
    [activeTabId, tabs],
  );

  const navigateToTab = useCallback(
    (tab: OpenTab) => {
      pendingNavigationRef.current = { tabId: tab.id, preserveHistory: true };
      setActiveTabId(tab.id);
      navigate(`${tab.pathname}${tab.search}`, { replace: true });
    },
    [navigate],
  );

  const openTab = useCallback(
    (pathname = DEFAULT_TAB_PATH, search = '') => {
      const tab = createTab(pathname, search);
      pendingNavigationRef.current = { tabId: tab.id, preserveHistory: true };
      setTabs((current) => {
        const next = [...current, tab];
        writeTabs(next);
        return next;
      });
      setActiveTabId(tab.id);
      navigate(`${tab.pathname}${tab.search}`, { replace: true });
    },
    [navigate],
  );

  const duplicateTab = useCallback(() => {
    openTab(location.pathname, location.search);
  }, [location.pathname, location.search, openTab]);

  const closeTab = useCallback(
    (id: string) => {
      setTabs((current) => {
        const closingIndex = current.findIndex((tab) => tab.id === id);

        if (closingIndex < 0) return current;

        if (current.length <= 1) {
          pendingNavigationRef.current = { tabId: null, preserveHistory: true };
          writeTabs([]);
          writeActiveTabId(null);
          setActiveTabId('');
          navigate(STANDALONE_HOME_PATH, { replace: true });
          return [];
        }

        const closingActive = id === activeTabId;
        const next = current.filter((tab) => tab.id !== id);
        writeTabs(next);

        if (closingActive) {
          const fallback = next[closingIndex] || next[closingIndex - 1] || next[0];
          pendingNavigationRef.current = { tabId: fallback.id, preserveHistory: true };
          setActiveTabId(fallback.id);
          if (location.pathname !== STANDALONE_HOME_PATH) {
            navigate(`${fallback.pathname}${fallback.search}`, { replace: true });
          }
        }

        return next;
      });
    },
    [activeTabId, location.pathname, navigate],
  );

  const reorderTabs = useCallback((activeId: string, overId: string) => {
    if (activeId === overId) return;

    setTabs((current) => {
      const activeIndex = current.findIndex((tab) => tab.id === activeId);
      const overIndex = current.findIndex((tab) => tab.id === overId);

      if (activeIndex < 0 || overIndex < 0) return current;

      const next = [...current];
      const [movedTab] = next.splice(activeIndex, 1);
      next.splice(overIndex, 0, movedTab);
      writeTabs(next);
      return next;
    });
  }, []);

  const closeOtherTabs = useCallback(() => {
    if (!activeTab) return;
    setTabs([activeTab]);
    writeTabs([activeTab]);
  }, [activeTab]);

  const closeAllTabs = useCallback(() => {
    pendingNavigationRef.current = { tabId: null, preserveHistory: true };
    setTabs([]);
    writeTabs([]);
    writeActiveTabId(null);
    setActiveTabId('');
    navigate(STANDALONE_HOME_PATH, { replace: true });
  }, [navigate]);

  const canGoBackInTab = Boolean(activeTab && activeTab.historyIndex > 0);
  const canGoForwardInTab = Boolean(activeTab && activeTab.historyIndex < activeTab.history.length - 1);

  const goBackInTab = useCallback(() => {
    if (!activeTab || activeTab.historyIndex <= 0) return;

    const historyIndex = activeTab.historyIndex - 1;
    const target = activeTab.history[historyIndex];
    pendingNavigationRef.current = { tabId: activeTab.id, preserveHistory: true };
    setTabs((current) => {
      const next = current.map((tab) =>
        tab.id === activeTab.id
          ? {
              ...tab,
              pathname: target.pathname,
              search: target.search,
              title: getTitle(target.pathname),
              updatedAt: Date.now(),
              historyIndex,
            }
          : tab,
      );
      writeTabs(next);
      return next;
    });
    navigate(toUrl(target), { replace: true });
  }, [activeTab, navigate]);

  const goForwardInTab = useCallback(() => {
    if (!activeTab || activeTab.historyIndex >= activeTab.history.length - 1) return;

    const historyIndex = activeTab.historyIndex + 1;
    const target = activeTab.history[historyIndex];
    pendingNavigationRef.current = { tabId: activeTab.id, preserveHistory: true };
    setTabs((current) => {
      const next = current.map((tab) =>
        tab.id === activeTab.id
          ? {
              ...tab,
              pathname: target.pathname,
              search: target.search,
              title: getTitle(target.pathname),
              updatedAt: Date.now(),
              historyIndex,
            }
          : tab,
      );
      writeTabs(next);
      return next;
    });
    navigate(toUrl(target), { replace: true });
  }, [activeTab, navigate]);

  return {
    tabs,
    activeTab,
    activeTabId,
    activeIndex,
    canGoBackInTab,
    canGoForwardInTab,
    goBackInTab,
    goForwardInTab,
    navigateToTab,
    openTab,
    duplicateTab,
    closeTab,
    reorderTabs,
    closeOtherTabs,
    closeAllTabs,
  };
}
